import React, { useEffect, useState, useCallback } from 'react';
import { useBranding } from '../contexts/BrandingContext';
import './InstallPWAButton.css';

// เด้ง popup ทุกครั้งที่เข้าใช้งาน จนกว่าจะ "ติดตั้งลงหน้าจอหลัก"
// - ติ๊ก "ไม่ต้องแสดงอีกใน 7 วัน" + ปิด  -> เงียบ 7 วัน แล้วกลับมาเด้งใหม่
// - ปิดเฉยๆ (ไม่ติ๊ก)                    -> เงียบเฉพาะ session นี้ เปิดใหม่/รีเฟรชก็เด้งอีก
// - ติดตั้งสำเร็จ / กด "ฉันติดตั้งแล้ว"   -> ไม่เด้งอีกเลย
const SNOOZE_KEY = 'imoney_pwa_install_dismissed_at';
const SNOOZE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 วัน
const INSTALLED_KEY = 'imoney_pwa_installed';
const SESSION_KEY = 'imoney_pwa_session_closed';
const SHOW_DELAY_MS = 1200; // หน่วงให้หน้าโหลดเสร็จ + รอ beforeinstallprompt ยิงก่อน

function isStandalone() {
    // กำลังเปิดจากไอคอนหน้าจอหลัก = ติดตั้งแล้วแน่นอน
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

function isIOS() {
    const ua = window.navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
    return /Android/i.test(window.navigator.userAgent);
}

function isInAppBrowser() {
    // in-app browser (Line / FB / IG ฯลฯ) ติดตั้งลงหน้าจอไม่ได้ — อย่ากวน
    return /FBAN|FBAV|FB_IAB|Line\/|Instagram|MicroMessenger|Twitter/i.test(window.navigator.userAgent);
}

function alreadyInstalled() {
    try { return localStorage.getItem(INSTALLED_KEY) === '1'; } catch (e) { return false; }
}
function markInstalled() {
    try { localStorage.setItem(INSTALLED_KEY, '1'); } catch (e) {}
}

function snoozeActive() {
    try {
        const at = parseInt(localStorage.getItem(SNOOZE_KEY) || '0', 10);
        return at && (Date.now() - at) < SNOOZE_TTL_MS;
    } catch (e) { return false; }
}
function setSnooze() {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now())); } catch (e) {}
}

function sessionClosed() {
    try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch (e) { return false; }
}
function setSessionClosed() {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (e) {}
}

// ควรเด้ง popup ไหม (ใช้ตัดสินใจตอน mount และตอน beforeinstallprompt ยิง)
function shouldShow() {
    if (isStandalone()) return false;      // เปิดจากไอคอนอยู่แล้ว
    if (alreadyInstalled()) return false;  // เคยติดตั้ง/กดยืนยันแล้ว
    if (isInAppBrowser()) return false;    // in-app browser ติดตั้งไม่ได้
    if (snoozeActive()) return false;      // อยู่ในช่วงเงียบ 7 วัน
    if (sessionClosed()) return false;     // ปิดไปแล้วใน session นี้
    // เด้งเฉพาะอุปกรณ์ที่ติดตั้งได้: iOS, Android หรือ desktop ที่มี deferredPrompt
    return isIOS() || isAndroid() || !!window.__pwaDeferredPrompt;
}

export default function InstallPWAButton() {
    const { appName } = useBranding();
    const name = appName || 'iMoni';

    const [deferredPrompt, setDeferredPrompt] = useState(() => window.__pwaDeferredPrompt || null);
    const [visible, setVisible] = useState(false);
    const [dontShow7d, setDontShow7d] = useState(false);

    const platform = isIOS() ? 'ios' : (isAndroid() ? 'android' : 'desktop');

    const close = useCallback((snooze) => {
        if (snooze) setSnooze();
        else setSessionClosed();
        setVisible(false);
    }, []);

    useEffect(() => {
        if (isStandalone()) { markInstalled(); return; }

        // beforeinstallprompt ถูกจับไว้ตั้งแต่ index.html แล้ว ส่งสัญญาณผ่าน event นี้
        const onInstallable = () => {
            setDeferredPrompt(window.__pwaDeferredPrompt || null);
            if (shouldShow()) setVisible(true);
        };
        const onInstalled = () => {
            markInstalled();
            setDeferredPrompt(null);
            setVisible(false);
        };

        window.addEventListener('pwa-installable', onInstallable);
        window.addEventListener('appinstalled', onInstalled);

        // เด้งหลังโหลดเสร็จเล็กน้อย (ทั้ง iOS และ Android)
        let timer = null;
        if (shouldShow()) {
            timer = setTimeout(() => { if (shouldShow()) setVisible(true); }, SHOW_DELAY_MS);
        }

        return () => {
            window.removeEventListener('pwa-installable', onInstallable);
            window.removeEventListener('appinstalled', onInstalled);
            if (timer) clearTimeout(timer);
        };
    }, []);

    // Android / Desktop: เรียก native install prompt
    const handleInstall = async () => {
        if (!deferredPrompt) return;
        try {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === 'accepted') {
                markInstalled();
                setVisible(false);
            }
            // ถ้า dismissed: ปล่อย popup ค้างไว้ ให้ผู้ใช้เลือกปิด/ติ๊ก 7 วันเอง
        } catch (e) {
            console.warn('Install prompt error:', e);
        }
        setDeferredPrompt(null);
        window.__pwaDeferredPrompt = null;
    };

    // iOS / กรณีต้องติดตั้งเอง: ผู้ใช้ยืนยันว่าทำเสร็จแล้ว -> ไม่กวนอีก
    const handleAlreadyInstalled = () => {
        markInstalled();
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <div className="pwa-pop-backdrop" role="dialog" aria-modal="true" aria-labelledby="pwa-pop-title">
            <div className="pwa-pop" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="pwa-pop-x"
                    onClick={() => close(dontShow7d)}
                    aria-label="ปิด"
                >
                    ×
                </button>

                <div className="pwa-pop-icon">
                    <img src="/icon-192.png" alt={name} width="64" height="64" />
                </div>

                <h3 id="pwa-pop-title" className="pwa-pop-title">เพิ่ม {name} ลงหน้าจอหลัก</h3>
                <p className="pwa-pop-lead">
                    เปิดใช้งานได้เร็วเหมือนแอป แตะไอคอนจากหน้าจอเครื่องได้เลย ไม่ต้องเปิดเบราว์เซอร์ทุกครั้ง
                </p>

                {deferredPrompt ? (
                    /* Android / Desktop ที่ติดตั้งได้ด้วยปุ่มเดียว */
                    <button type="button" className="pwa-pop-primary" onClick={handleInstall}>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 3v12" />
                            <path d="M7 10l5 5 5-5" />
                            <path d="M5 21h14" />
                        </svg>
                        ติดตั้งแอปเลย
                    </button>
                ) : platform === 'ios' ? (
                    /* iOS Safari: ทำเองตามขั้นตอน */
                    <ol className="pwa-pop-steps">
                        <li>
                            แตะปุ่ม <strong>แชร์</strong>
                            <svg viewBox="0 0 24 24" width="17" height="17" className="pwa-pop-inline-ic" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                <polyline points="16 6 12 2 8 6" />
                                <line x1="12" y1="2" x2="12" y2="15" />
                            </svg>
                            ที่แถบด้านล่างของ Safari
                        </li>
                        <li>เลื่อนหา <strong>"เพิ่มไปยังหน้าจอโฮม"</strong> (Add to Home Screen)</li>
                        <li>แตะ <strong>"เพิ่ม"</strong> มุมขวาบน</li>
                    </ol>
                ) : (
                    /* Android/Desktop ที่ยังไม่มี native prompt: แนะนำเมนู */
                    <ol className="pwa-pop-steps">
                        <li>แตะเมนู <strong>⋮</strong> มุมขวาบนของเบราว์เซอร์</li>
                        <li>เลือก <strong>"ติดตั้งแอป"</strong> หรือ <strong>"เพิ่มลงในหน้าจอหลัก"</strong></li>
                        <li>ยืนยัน <strong>"ติดตั้ง"</strong></li>
                    </ol>
                )}

                {!deferredPrompt && (
                    <button type="button" className="pwa-pop-secondary" onClick={handleAlreadyInstalled}>
                        ฉันติดตั้งแล้ว
                    </button>
                )}

                <label className="pwa-pop-check">
                    <input
                        type="checkbox"
                        checked={dontShow7d}
                        onChange={(e) => setDontShow7d(e.target.checked)}
                    />
                    <span>ไม่ต้องแสดงอีกใน 7 วัน</span>
                </label>

                <button type="button" className="pwa-pop-later" onClick={() => close(dontShow7d)}>
                    ไว้ภายหลัง
                </button>
            </div>
        </div>
    );
}
